import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';

export default function AdminMemberView() {
  const { id }      = useParams();
  const [data, setData]   = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [form, setForm]   = useState({ plan_id:'', amount_paid:'', payment_note:'', start_date: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]     = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`/admin/members/${id}`),
      axios.get('/admin/plans')
    ]).then(([m, p]) => { setData(m.data); setPlans(p.data); setLoading(false); });
  }, [id]);

  const assignMembership = async e => {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      const r = await axios.post(`/admin/members/${id}/membership`, form);
      setMsg(`✅ Membership activated. Expires: ${r.data.expiry_date}`);
      const m = await axios.get(`/admin/members/${id}`);
      setData(m.data);
      setShowAssign(false);
    } catch (err) { setMsg(err.response?.data?.message || 'Error.'); }
    finally { setSaving(false); }
  };

  const suspend = async () => {
    if (!window.confirm('Suspend this member?')) return;
    await axios.patch(`/admin/members/${id}/suspend`);
    const m = await axios.get(`/admin/members/${id}`); setData(m.data);
  };

  const markAttendance = async () => {
    try { await axios.post('/admin/attendance/manual', { user_id: id }); alert('Attendance marked.'); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  if (loading) return <div className="loading-wrap"><div className="spinner"></div></div>;
  if (!data) return <div className="empty-state"><p>Member not found.</p></div>;

  const { member, memberships, attendance } = data;
  const active = memberships.find(m => m.status === 'active');

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <Link to="/admin/members" style={{ fontSize:13, color:'var(--text-secondary)' }}>← Back to members</Link>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:18 }}>
        {/* Member info card */}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div className="card" style={{ padding:22, textAlign:'center' }}>
            <div className="avatar" style={{ width:64, height:64, fontSize:22, margin:'0 auto 14px' }}>
              {member.photo ? <img src={member.photo} alt={member.name} /> : member.name.slice(0,2).toUpperCase()}
            </div>
            <div style={{ fontSize:17, fontWeight:600, marginBottom:4 }}>{member.name}</div>
            <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:12 }}>{member.email}</div>
            <span className={`badge ${member.is_active ? 'badge-success' : 'badge-danger'}`}>
              {member.is_active ? 'Active account' : 'Suspended'}
            </span>
            <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:8 }}>
              <button className="btn-primary" style={{ justifyContent:'center' }} onClick={() => setShowAssign(true)}>💳 Assign membership</button>
              <button className="btn-ghost" style={{ justifyContent:'center' }} onClick={markAttendance}>📋 Mark today's attendance</button>
              {member.is_active && <button className="btn-danger" style={{ justifyContent:'center' }} onClick={suspend}>🚫 Suspend member</button>}
            </div>
          </div>

          <div className="card" style={{ padding:18 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:12 }}>Member details</div>
            {[['📞 Phone', member.phone||'—'], ['⚥ Gender', member.gender], ['🎯 Goal', (member.fitness_goal||'').replace('_',' ')], ['📅 Joined', new Date(member.created_at).toLocaleDateString('en-GB')], ['🚨 Emergency', member.emergency_contact||'—']].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span style={{ color:'var(--text-muted)' }}>{k}</span>
                <span style={{ color:'var(--text-primary)', textAlign:'right', textTransform:'capitalize' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {/* Active membership */}
          <div className="card">
            <div className="card-header"><div className="card-title">💳 Current membership</div></div>
            <div style={{ padding:18 }}>
              {active ? (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
                  {[['Plan', active.plan_name], ['Start', new Date(active.start_date).toLocaleDateString('en-GB')], ['Expires', new Date(active.expiry_date).toLocaleDateString('en-GB')], ['Amount paid', `₨${Number(active.amount_paid).toLocaleString()}`], ['Payment', active.payment_method], ['Status', active.status]].map(([k,v]) => (
                    <div key={k} style={{ background:'var(--surface-2)', borderRadius:'var(--radius)', padding:'10px 14px' }}>
                      <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>{k}</div>
                      <div style={{ fontSize:14, fontWeight:600, textTransform:'capitalize' }}>{v}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{ padding:24 }}>
                  <p>No active membership. <button className="btn-primary" style={{ display:'inline-flex', marginTop:10 }} onClick={() => setShowAssign(true)}>Assign one →</button></p>
                </div>
              )}
            </div>
          </div>

          {/* Membership history */}
          <div className="card">
            <div className="card-header"><div className="card-title">📜 Membership history</div></div>
            <table className="table">
              <thead><tr><th>Plan</th><th>Start</th><th>Expiry</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>
                {memberships.length === 0 && <tr><td colSpan={5} style={{ textAlign:'center', color:'var(--text-muted)', padding:20 }}>No history.</td></tr>}
                {memberships.map(m => (
                  <tr key={m.id}>
                    <td className="name">{m.plan_name}</td>
                    <td>{new Date(m.start_date).toLocaleDateString('en-GB')}</td>
                    <td>{new Date(m.expiry_date).toLocaleDateString('en-GB')}</td>
                    <td>₨{Number(m.amount_paid).toLocaleString()}</td>
                    <td><span className={`badge ${m.status==='active'?'badge-success':m.status==='expired'?'badge-muted':'badge-danger'}`}>{m.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent attendance */}
          <div className="card">
            <div className="card-header"><div className="card-title">📅 Recent attendance ({attendance.length} sessions)</div></div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, padding:16 }}>
              {attendance.length === 0 && <div style={{ color:'var(--text-muted)', fontSize:13 }}>No attendance recorded.</div>}
              {attendance.slice(0,30).map(a => (
                <div key={a.id} style={{ background:'var(--success-dim)', border:'1px solid rgba(34,197,94,.2)', borderRadius:6, padding:'4px 10px', fontSize:12, color:'var(--success)' }}>
                  {new Date(a.date).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Assign membership modal */}
      {showAssign && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div className="card" style={{ width:'100%', maxWidth:440 }}>
            <div className="card-header">
              <div className="card-title">💳 Assign membership — {member.name}</div>
              <button onClick={() => setShowAssign(false)} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:18 }}>✕</button>
            </div>
            <form onSubmit={assignMembership} style={{ padding:22 }}>
              {msg && <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:'var(--radius)', background: msg.includes('✅') ? 'var(--success-dim)' : 'var(--danger-dim)', color: msg.includes('✅') ? 'var(--success)' : 'var(--danger)', fontSize:13 }}>{msg}</div>}
              <div className="form-group">
                <label className="label">Plan *</label>
                <select className="select" value={form.plan_id} onChange={e => { setForm(p => ({...p, plan_id: e.target.value, amount_paid: plans.find(pl => pl.id == e.target.value)?.price || ''})); }} required>
                  <option value="">Select a plan</option>
                  {plans.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{p.name} — ₨{Number(p.price).toLocaleString()} ({p.duration_days} days)</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="label">Start date *</label>
                  <input className="input" type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} required />
                </div>
                <div className="form-group">
                  <label className="label">Amount collected (₨) *</label>
                  <input className="input" type="number" value={form.amount_paid} onChange={e => setForm(p => ({...p, amount_paid: e.target.value}))} required />
                </div>
              </div>
              <div className="form-group">
                <label className="label">Payment note (optional)</label>
                <input className="input" placeholder="e.g. Paid in full — cash" value={form.payment_note} onChange={e => setForm(p => ({...p, payment_note: e.target.value}))} />
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button className="btn-primary" type="submit" disabled={saving} style={{ flex:1, justifyContent:'center' }}>{saving ? 'Saving…' : 'Activate membership'}</button>
                <button type="button" className="btn-ghost" onClick={() => setShowAssign(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
